import { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import * as warehouseApi from "@/lib/api/warehouse";
import { useAuth } from "@/context/AuthContext";
import { canWorkSales } from "@/lib/permissions";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { LoadingState } from "@/components/base/LoadingState";
import { Modal } from "@/components/base/Modal";
import { ConfirmDialog } from "@/components/base/ConfirmDialog";
import { money, fmtDateTime } from "@/lib/format";
import {
  selectShippingQuote,
  createShipment,
  createShipmentPackage,
  updateShipmentPackage,
  deleteShipmentPackage,
  addressFromLocation,
  formatAddress,
  formatEta,
  zoneLabel,
  type RateQuote,
} from "@/lib/shipping";
import { resolveShipmentDestination } from "@/lib/sales-order-edit";
import { carrierLabel, RATE_SOURCE_LABEL } from "@/lib/carriers";
import { resolveParcelConstraints } from "@/lib/parcelization";
import { RatePanel } from "./components/RatePanel";
import { PackagesSection, type PackageRow, type ParcelType, type FulfilmentItem, type PackageDraft, type PackageItem } from "./components/PackagesSection";
import { restoreManualTrackingCarrier, storedManualTrackingMatches } from "@/lib/manual-tracking";
import { MANUAL_SHIPPING_CARRIERS, mapCarrierToBcShippingProvider } from "@/lib/providers/bigcommerce-fulfilment";
import { syncBigCommerceFulfilment, syncEbayFulfilment } from "@/lib/api/marketplace-fulfilment";
import { retryChannelSyncLabel, shipmentChannelPendingCopy, shipmentTrackingSavedCopy, trackingCta } from "@/lib/phase1-sync-contract";
import {
  authoritativeShippedAt,
  shipmentCarrierLabel,
  shipmentLabelStateLabel,
  shipmentLifecycleComplete,
  shipmentOperatorStatus,
  shipmentPostageLabel,
  shipmentServiceLabel,
  tradivoLabelPurchased,
} from "@/lib/warehouse-projection";
import { fulfilmentWorkspacePath } from "@/lib/fulfilment-workspace";
import {
  australiaPostTrackingUrl,
  isAustraliaPostShipment,
  projectShipmentTimelines,
  viewPrintLabelAction,
  type ProjectedTimelineEvent,
  type TrackingEventRow,
} from "@/lib/shipment-timeline";

interface ShipmentRow {
  id: string;
  fulfilment_id: string;
  idempotency_key?: string | null;
  carrier_service_id: string | null;
  carrier_account_id: string | null;
  carrier_reference: string | null;
  label_type: string | null;
  tracking_status: string | null;
  last_tracked_at: string | null;
  tracking_recorded_at?: string | null;
  status: string;
  tracking_number: string | null;
  label_url: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  currency: string;
  shipping_cost: number | null;
  service_name: string | null;
  shipping_provider?: string | null;
  provider_sync_status?: string | null;
  provider_sync_error?: string | null;
  provider_shipment_id?: string | null;
  ship_from: Record<string, unknown> | null;
  ship_to: Record<string, unknown> | null;
  carrier_services?: { name: string; carrier_code: string } | null;
  carrier_accounts?: { name: string; carrier: string; auth_status: string } | null;
  fulfilments?: { id: string; fulfilment_number: string; location_id: string | null; sales_order_id: string | null } | null;
}

interface QuoteRow {
  id: string;
  carrier_code: string | null;
  service_name: string | null;
  zone: string | null;
  currency: string | null;
  cost: number | null;
  chargeable_weight_kg: number | null;
  eta_min_days: number | null;
  eta_max_days: number | null;
  is_selected: boolean;
  rate_source: string | null;
  created_at: string;
}

export default function ShipmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { org, role } = useAuth();
  const queryClient = useQueryClient();
  const recordedTracking = useRef<{ orgId: string | undefined; shipmentId: string; number: string; provider: string; method: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [exceptionOpen, setExceptionOpen] = useState(false);
  const [exceptionNote, setExceptionNote] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [trackingValue, setTrackingValue] = useState("");
  const [trackingCarrier, setTrackingCarrier] = useState("");
  const [trackingCustom, setTrackingCustom] = useState("");
  const [error, setError] = useState("");
  const [selectedQuote, setSelectedQuote] = useState<{ quoteId: string | null; carrierAccountId: string | null } | null>(null);
  const [createFeedback, setCreateFeedback] = useState("");

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["shipment-detail-page", org?.id, id],
    enabled: !!org?.id && !!id,
    queryFn: async () => {
      const ship = await supabase
        .from("shipments")
        .select("*, carrier_services(name, carrier_code), carrier_accounts(name, carrier, auth_status), fulfilments(id, fulfilment_number, location_id, sales_order_id)")
        .eq("org_id", org!.id)
        .eq("id", id)
        .maybeSingle();
      const shipment = ship.data as ShipmentRow | null;
      const fulfilment = shipment?.fulfilments ?? null;
      const locationId = fulfilment?.location_id ?? null;
      const soId = fulfilment?.sales_order_id ?? null;
      const fulfilmentId = fulfilment?.id ?? null;

      const [packages, events, parcelTypes, quotes, location, salesOrder, fulfilmentItems] = await Promise.all([
        supabase.from("shipment_packages").select("*").eq("org_id", org!.id).eq("shipment_id", id).order("package_number"),
        supabase.from("tracking_events").select("*").eq("org_id", org!.id).eq("shipment_id", id).order("occurred_at", { ascending: false }),
        supabase.from("parcel_types").select("*").eq("org_id", org!.id).eq("is_active", true).order("name"),
        supabase.from("shipping_rate_quotes").select("*").eq("org_id", org!.id).eq("shipment_id", id).order("created_at", { ascending: false }),
        locationId ? supabase.from("locations").select("*").eq("id", locationId).maybeSingle() : Promise.resolve({ data: null }),
        soId ? supabase.from("sales_orders").select("id, order_number, customer_id, shipping_address, source, channel_id").eq("id", soId).maybeSingle() : Promise.resolve({ data: null }),
        fulfilmentId
          ? supabase.from("fulfilment_items").select("catalogue_product_identity_id, catalogue_variant_identity_id, product_name_snapshot, sku_snapshot, variant_attributes_snapshot, catalogue_snapshot_origin, id, product_id, variant_id, qty_fulfilled, products(name, sku, weight_kg, length_cm, width_cm, height_cm), variants(weight_kg, length_cm, width_cm, height_cm)").eq("org_id", org!.id).eq("fulfilment_id", fulfilmentId).order("created_at")
          : Promise.resolve({ data: [] }),
      ]);

      let customer: { id: string; name: string | null; shipping_address: Record<string, unknown> | null } | null = null;
      if (salesOrder.data?.customer_id) {
        const c = await supabase.from("customers").select("id, name, shipping_address").eq("id", salesOrder.data.customer_id).maybeSingle();
        customer = (c.data ?? null) as typeof customer;
      }

      const eventRows = (events.data ?? []) as TrackingEventRow[];
      const trackingRecorded = eventRows.find((ev) => ev.event_code === "TRACKING_RECORDED")?.occurred_at ?? null;
      return {
        shipment: shipment
          ? { ...shipment, tracking_recorded_at: trackingRecorded }
          : null,
        packages: (packages.data ?? []) as PackageRow[],
        events: eventRows,
        parcelTypes: (parcelTypes.data ?? []) as ParcelType[],
        quotes: (quotes.data ?? []) as QuoteRow[],
        location: location.data ?? null,
        salesOrder: salesOrder.data ?? null,
        customer: customer ?? null,
        fulfilmentItems: (fulfilmentItems.data ?? []) as FulfilmentItem[],
      };
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["shipment-detail-page", org?.id, id] });

  const updateStatus = async (status: string, eventDescription = "") => {
    setBusy(true);
    setError("");
    try {
      await warehouseApi.updateShipmentStatus(id!, status, eventDescription || null);
      refresh();
    } catch (e) {
      setError((e as Error).message);
    }
    setBusy(false);
  };

  const selectQuote = async (q: RateQuote, signature: string) => {
    setBusy(true);
    setError("");
    setCreateFeedback("");
    try {
      const res = await selectShippingQuote({ shipmentId: id!, quote: q, signature });
      if (!res.ok) {
        setError(res.error ?? "Couldn't select this service.");
      } else {
        setSelectedQuote({ quoteId: res.quote_id ?? null, carrierAccountId: q.carrier_account_id ?? null });
        setCreateFeedback('Service selected. Choose "Create shipment" to finalise.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't select this service.");
    }
    setBusy(false);
    refresh();
  };

  const doCreateShipment = async () => {
    setBusy(true);
    setError("");
    setCreateFeedback("");
    try {
      const res = await createShipment({
        shipmentId: id!,
        carrierAccountId: selectedQuote?.carrierAccountId ?? null,
        quoteId: selectedQuote?.quoteId ?? null,
      });
      if (!res.ok) {
        setError(res.error ?? "Couldn't create shipment.");
      } else if (res.already_created) {
        setCreateFeedback("Shipment already created.");
      } else if (res.mode === "live" && res.queued) {
        setCreateFeedback("Sent to the carrier for processing — label and tracking will appear shortly.");
      } else {
        setCreateFeedback("Shipment created — internal label ready to print.");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't create shipment.");
    }
    setBusy(false);
    refresh();
  };

  const savePackage = async (draft: PackageDraft, items: PackageItem[], editingId: string | null): Promise<boolean> => {
    setBusy(true);
    const payload = {
      package_number: draft.package_number.trim(),
      weight: draft.weight.trim() ? Number(draft.weight) : null,
      length_cm: draft.length_cm.trim() ? Number(draft.length_cm) : null,
      width_cm: draft.width_cm.trim() ? Number(draft.width_cm) : null,
      height_cm: draft.height_cm.trim() ? Number(draft.height_cm) : null,
      declared_value: draft.declared_value.trim() ? Number(draft.declared_value) : null,
      tracking_number: draft.tracking_number.trim() || null,
      items,
    };
    try {
      const res = editingId
        ? await updateShipmentPackage(editingId, payload)
        : await createShipmentPackage(id!, payload);
      setBusy(false);
      if (!res.ok) {
        setError(res.error ?? "Couldn't save the package.");
        return false;
      }
      refresh();
      return true;
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : "Couldn't save the package.");
      return false;
    }
  };

  const deletePackage = async (pkgId: string) => {
    setBusy(true);
    try {
      const res = await deleteShipmentPackage(pkgId);
      if (!res.ok) setError(res.error ?? "Couldn't remove the package.");
      else refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't remove the package.");
    }
    setBusy(false);
  };

  const markException = async () => {
    if (!exceptionNote.trim()) return;
    setBusy(true);
    setError("");
    try {
      await warehouseApi.updateShipmentStatus(id!, "exception", exceptionNote.trim());
      setExceptionOpen(false);
      setExceptionNote("");
      refresh();
    } catch (e) {
      setError((e as Error).message);
    }
    setBusy(false);
  };

  const openTracking = () => {
    const carrier = restoreManualTrackingCarrier(data?.shipment);
    recordedTracking.current = null;
    setTrackingValue(data?.shipment?.tracking_number ?? "");
    setTrackingCarrier(carrier.code);
    setTrackingCustom(carrier.custom);
    setError("");
    setTrackingOpen(true);
  };

  const recordTracking = async () => {
    const val = trackingValue.trim();
    if (!val) return;
    setBusy(true);
    setError("");
    try {
      const receipt = recordedTracking.current;
      const carrier = trackingCarrier && (trackingCarrier !== "other" || trackingCustom.trim())
        ? mapCarrierToBcShippingProvider(trackingCarrier, trackingCustom) : null;
      const acknowledged = !!receipt && !!org?.id && !!id && receipt.orgId === org.id && receipt.shipmentId === id;
      const sameRecord = acknowledged
        ? !!carrier && receipt.number === val && receipt.provider === carrier.shipping_provider && receipt.method === carrier.shipping_method
        : storedManualTrackingMatches(data?.shipment, val, trackingCarrier, trackingCustom);
      if (sameRecord && !acknowledged && data?.shipment?.provider_sync_status === "confirmed") {
        setTrackingOpen(false);
        setBusy(false);
        return;
      }
      if (!sameRecord) {
        if (trackingCarrier === "other" && !trackingCustom.trim()) throw new Error("Enter the custom carrier name.");
        if (!carrier) throw new Error("Select a carrier before saving tracking.");
        const recorded = await warehouseApi.setShipmentTracking(id!, val, trackingCarrier, trackingCarrier === "other" ? trackingCustom : "Australia Post");
        recordedTracking.current = { orgId: org?.id, shipmentId: id!, number: recorded.tracking_number,
          provider: carrier.shipping_provider, method: carrier.shipping_method };
      }
      const so = data?.salesOrder as { source?: string; channel_id?: string | null; id?: string } | undefined;
      if (so?.source === "bigcommerce" && so.channel_id && org?.id) {
        const sync = await syncBigCommerceFulfilment({
          connectorId: so.channel_id,
          orgId: org.id,
          salesOrderId: so.id!,
          shipmentId: id,
        });
        if (!sync.success) {
          setError(sync.error || "Tracking saved in Tradivo. Channel sync could not start. Retry shipment sync.");
          setBusy(false);
          refresh();
          return;
        }
      }
      if (so?.source === "ebay" && so.channel_id && org?.id) {
        const sync = await syncEbayFulfilment({
          connectorId: so.channel_id,
          orgId: org.id,
          salesOrderId: so.id!,
          shipmentId: id,
        });
        if (!sync.success) {
          setError(sync.error || "Tracking saved in Tradivo. Channel sync could not start. Retry shipment sync.");
          setBusy(false);
          refresh();
          return;
        }
      }
      setTrackingOpen(false);
      setTrackingValue("");
      refresh();
    } catch (e) {
      setError((e as Error).message);
    }
    setBusy(false);
  };

  const retryChannelSync = async () => {
    const so = data?.salesOrder as { source?: string; channel_id?: string | null; id?: string } | undefined;
    if (!so?.id || !org?.id || !id) return;
    setBusy(true);
    setError("");
    try {
      if (so.source === "bigcommerce" && so.channel_id) {
        const sync = await syncBigCommerceFulfilment({
          connectorId: so.channel_id,
          orgId: org.id,
          salesOrderId: so.id,
          shipmentId: id,
        });
        if (!sync.success) throw new Error(sync.error || "Tracking saved in Tradivo. Channel sync could not start. Retry shipment sync.");
      } else if (so.source === "ebay" && so.channel_id) {
        const sync = await syncEbayFulfilment({
          connectorId: so.channel_id,
          orgId: org.id,
          salesOrderId: so.id,
          shipmentId: id,
        });
        if (!sync.success) throw new Error(sync.error || "Tracking saved in Tradivo. Channel sync could not start. Retry shipment sync.");
      }
      refresh();
    } catch (e) {
      setError((e as Error).message);
      refresh();
    }
    setBusy(false);
  };

  if (isLoading) return <LoadingState />;

  if (isError || !data?.shipment) {
    return (
      <div>
        <PageHeader title="Shipment" backTo={fulfilmentWorkspacePath("shipments")} />
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load this shipment.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const s = data.shipment;
  const timelines = projectShipmentTimelines(data.events);
  const ausPost = isAustraliaPostShipment(s);
  const ausPostTrackUrl = ausPost ? australiaPostTrackingUrl(s.tracking_number) : null;
  const labelAction = viewPrintLabelAction(s);
  const openLabel = () => {
    if (labelAction.kind === "open-url") {
      window.open(labelAction.url, "_blank", "noopener,noreferrer");
      return;
    }
    setError(labelAction.message);
  };
  const carrierCode = s.carrier_accounts?.carrier ?? s.carrier_services?.carrier_code ?? s.shipping_provider ?? null;
  const autoPackConstraints = resolveParcelConstraints(carrierCode);
  const carrierName = shipmentCarrierLabel(s);
  const serviceLabel = shipmentServiceLabel(s);
  const operatorStatus = shipmentOperatorStatus(s);
  const shippedAt = authoritativeShippedAt(s);
  const shipFrom = (s.ship_from as Record<string, unknown>) ?? addressFromLocation(data.location) ?? null;
  const shipTo = resolveShipmentDestination(
    s.ship_to as Record<string, unknown> | null,
    data.salesOrder?.shipping_address as Record<string, unknown> | null | undefined,
    data.customer?.shipping_address as Record<string, unknown> | null,
  );

  const status = s.status;
  const canOperate = canWorkSales(role);
  const terminal = status === "delivered" || status === "cancelled" || shipmentLifecycleComplete(s);
  // Provider confirmation completes dispatch, but the RPC still permits manual
  // tracking corrections until delivery/cancellation. Keep those controls separate.
  const canEditTracking = canOperate && status !== "delivered" && status !== "cancelled";
  const canCreateShipment = canOperate && !terminal && !!selectedQuote && !s.idempotency_key && !s.carrier_reference;
  const canShip = canOperate && !terminal && ["packed", "ready", "rated", "label_printed", "exception"].includes(status);
  const canInTransit = canOperate && !terminal && status === "shipped";
  const canDeliver = canOperate && !terminal && ["shipped", "in_transit", "exception"].includes(status);
  const actions = (
    <div className="flex items-center gap-2 flex-wrap">
      {canCreateShipment && (
        <button onClick={doCreateShipment} disabled={busy} className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-1.5">
          <i className="ri-send-plane-line" /> Create shipment
        </button>
      )}
      {canShip && (
        <button onClick={() => updateStatus("shipped")} disabled={busy} className="px-3 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
          Mark Shipped
        </button>
      )}
      {canInTransit && (
        <button onClick={() => updateStatus("in_transit")} disabled={busy} className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
          Mark In Transit
        </button>
      )}
      {canDeliver && (
        <button onClick={() => updateStatus("delivered")} disabled={busy} className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
          Mark Delivered
        </button>
      )}
      {canOperate && !terminal && (
        <button onClick={() => setExceptionOpen(true)} disabled={busy} className="px-3 py-2 bg-red-50 hover:bg-red-100 disabled:opacity-60 text-red-700 text-sm font-medium rounded-lg whitespace-nowrap">
          Exception
        </button>
      )}
      {canOperate && !terminal && (
        <button onClick={() => setCancelOpen(true)} disabled={busy} className="px-3 py-2 bg-white border border-slate-200 hover:bg-red-50 hover:text-red-700 hover:border-red-200 text-slate-600 text-sm font-medium rounded-lg whitespace-nowrap">
          Cancel Shipment
        </button>
      )}
    </div>
  );

  return (
    <div>
      <PageHeader title={s.tracking_number ?? "Shipment"} subtitle={carrierName} backTo={fulfilmentWorkspacePath("shipments")}>
        {actions}
      </PageHeader>

      {error && <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}

      {createFeedback && <div className="mb-4 px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-emerald-700">{createFeedback}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <StatusBadge status={operatorStatus.badge} customLabel={operatorStatus.label} />
              <span className="text-xs text-slate-400">Shipped {fmtDateTime(shippedAt)}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-slate-400">Order</p>
                <button onClick={() => data.salesOrder && navigate(`/sales-orders/${data.salesOrder.id}`)} className="text-sm font-medium text-emerald-600 hover:text-emerald-700 font-mono whitespace-nowrap">
                  {data.salesOrder?.order_number ?? "—"}
                </button>
              </div>
              <div>
                <p className="text-xs text-slate-400">Fulfilment</p>
                <p className="text-sm font-medium text-slate-800 font-mono">{s.fulfilments?.fulfilment_number ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Service</p>
                <p className="text-sm font-medium text-slate-800">{serviceLabel}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Carrier</p>
                <p className="text-sm font-medium text-slate-800">{carrierName}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Label</p>
                <p className="text-sm font-medium text-slate-800">{shipmentLabelStateLabel(s)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Tracking</p>
                {s.tracking_number ? (
                  <div className="space-y-1">
                    {ausPost && <p className="text-sm font-medium text-slate-800">Carrier: Australia Post</p>}
                    <p className="text-sm font-medium text-slate-800 font-mono">{s.tracking_number}</p>
                    {ausPostTrackUrl && (
                      <a
                        href={ausPostTrackUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600 hover:text-emerald-700"
                      >
                        Track on Australia Post <i className="ri-external-link-line text-xs" />
                      </a>
                    )}
                    {ausPost && (
                      <p className="text-[11px] text-slate-400">External carrier tracking. Tradivo does not invent scan statuses.</p>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={openTracking}
                    disabled={!canEditTracking || busy}
                    className="text-sm font-medium text-emerald-600 hover:text-emerald-700 whitespace-nowrap disabled:opacity-50"
                  >
                    Record tracking
                  </button>
                )}
              </div>
              {s.tracking_number && canEditTracking && (
                <button type="button" onClick={openTracking} disabled={busy}
                  className="text-sm font-medium text-emerald-600 hover:text-emerald-700 disabled:opacity-50">
                  Update tracking
                </button>
              )}
              <div>
                <p className="text-xs text-slate-400">Postage cost</p>
                <p className="text-sm font-medium text-slate-800">{shipmentPostageLabel(s)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Provider shipment</p>
                <p className="text-sm font-medium text-slate-800 font-mono">{s.provider_shipment_id ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Provider sync</p>
                <p className="text-sm font-medium text-slate-800">{s.provider_sync_status ?? "—"}</p>
                {s.tracking_number && s.provider_sync_status !== "confirmed" && (
                  <p className="text-xs text-amber-700 mt-1">{shipmentChannelPendingCopy((data?.salesOrder as { source?: string } | undefined)?.source)}</p>
                )}
                {canOperate && trackingCta({ trackingNumber: s.tracking_number, providerSyncStatus: s.provider_sync_status }) === "retry" && (
                  <button
                    type="button"
                    onClick={() => void retryChannelSync()}
                    disabled={busy}
                    className="mt-2 px-3 py-1 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg"
                  >
                    {retryChannelSyncLabel()}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Addresses</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="border border-slate-100 rounded-lg p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">From</p>
                <p className="text-sm text-slate-700 leading-snug">{formatAddress(shipFrom)}</p>
              </div>
              <div className="border border-slate-100 rounded-lg p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Ship to</p>
                <p className="text-sm text-slate-700 leading-snug">{formatAddress(shipTo)}</p>
              </div>
            </div>
          </div>

          <PackagesSection
            packages={data.packages}
            parcelTypes={data.parcelTypes}
            fulfilmentItems={data.fulfilmentItems}
            saving={busy}
            onSave={savePackage}
            onDelete={deletePackage}
            autoPackConstraints={autoPackConstraints}
          />

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-1">Tradivo activity</h3>
            <p className="text-xs text-slate-400 mb-3">Tracking recorded, provider sync, and fulfilment/shipment events. Duplicate view/print artefacts are collapsed; audit rows are retained.</p>
            <TimelineList events={timelines.tradivo} empty="No Tradivo activity yet" />
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-1">Carrier tracking</h3>
            <p className="text-xs text-slate-400 mb-3">Physical Australia Post scans only. Internal Tradivo events are not shown as carrier scans.</p>
            <TimelineList events={timelines.carrier} empty={ausPost ? "No Australia Post scan events in Tradivo yet. Use Track on Australia Post for live carrier tracking." : "No carrier scan events"} />
          </div>
        </div>

        <div className="space-y-6">
          <RatePanel
            shipmentId={s.id}
            originLabel={data.location?.name ?? "Unknown origin"}
            onSelect={selectQuote}
            busy={busy}
          />

          {data.quotes.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Quote History</h3>
              <div className="space-y-2">
                {data.quotes.map((q) => (
                  <div key={q.id} className="border border-slate-100 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-slate-800">{q.service_name ?? "—"}</p>
                        {q.rate_source && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                            {RATE_SOURCE_LABEL[q.rate_source] ?? q.rate_source}
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-bold text-slate-900">{q.cost != null ? `${q.currency} ${money(q.cost, q.currency)}` : "—"}</p>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {q.carrier_code ? `${carrierLabel(q.carrier_code)} · ` : ""}
                      {q.zone ? `${zoneLabel(q.zone)} · ` : ""}
                      {formatEta(q.eta_min_days, q.eta_max_days)}
                      {q.chargeable_weight_kg != null ? ` · ${q.chargeable_weight_kg} kg` : ""}
                    </p>
                    {q.is_selected && <span className="inline-block mt-1.5 text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">Selected</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-2">Shipping label</h3>
            {labelAction.kind === "open-url" ? (
              <>
                <p className="text-xs text-slate-500 mb-3">Opens the purchased carrier label. Viewing or printing does not create shipment events.</p>
                <button onClick={openLabel} className="w-full px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center justify-center gap-2">
                  <i className="ri-printer-line" /> View &amp; print shipping label
                </button>
              </>
            ) : (
              <p className="text-sm text-slate-600">
                {tradivoLabelPurchased(s) ? labelAction.message : "No shipping label purchased in Tradivo"}
              </p>
            )}
          </div>
        </div>
      </div>

      <Modal
        open={exceptionOpen}
        onClose={() => setExceptionOpen(false)}
        title="Record Exception"
        footer={
          <>
            <button onClick={() => setExceptionOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
              Cancel
            </button>
            <button onClick={markException} disabled={busy || !exceptionNote.trim()} className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {busy ? "Recording..." : "Record Exception"}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">Describe the issue. This flags the shipment and adds a tracking event.</p>
          <textarea
            value={exceptionNote}
            onChange={(e) => setExceptionNote(e.target.value.slice(0, 500))}
            rows={4}
            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
            placeholder="e.g. Address undeliverable, awaiting customer contact"
          />
          <p className="text-xs text-slate-400 text-right">{exceptionNote.length}/500</p>
        </div>
      </Modal>

      <Modal
        open={trackingOpen}
        onClose={() => setTrackingOpen(false)}
        title="Record Tracking Number"
        footer={
          <>
            <button onClick={() => setTrackingOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
              Cancel
            </button>
            <button onClick={recordTracking} disabled={busy || !trackingValue.trim()} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {busy ? "Saving..." : (data?.salesOrder as { source?: string } | null)?.source === "bigcommerce" ? "Save and send" : "Save tracking"}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          <p className="text-sm text-slate-600">{shipmentTrackingSavedCopy()}</p>
          <select
            value={trackingCarrier}
            onChange={(e) => setTrackingCarrier(e.target.value)}
            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
          >
            <option value="">Select carrier (not recorded)</option>
            {MANUAL_SHIPPING_CARRIERS.map((c) => (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
          </select>
          {trackingCarrier === "other" && (
            <input
              type="text"
              value={trackingCustom}
              onChange={(e) => setTrackingCustom(e.target.value)}
              placeholder="Carrier name"
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
            />
          )}
          <input
            type="text"
            value={trackingValue}
            onChange={(e) => setTrackingValue(e.target.value)}
            placeholder="e.g. ABC123456789"
            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={cancelOpen}
        title="Cancel Shipment"
        message="Cancel this shipment? Its tracking will be marked cancelled and it can no longer be dispatched."
        confirmLabel="Cancel Shipment"
        loading={busy}
        onCancel={() => setCancelOpen(false)}
        onConfirm={async () => {
          setBusy(true);
          await updateStatus("cancelled");
          setBusy(false);
          setCancelOpen(false);
        }}
      />
    </div>
  );
}

function TimelineList({ events, empty }: { events: ProjectedTimelineEvent[]; empty: string }) {
  if (events.length === 0) {
    return <div className="px-4 py-5 text-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg">{empty}</div>;
  }
  return (
    <div className="space-y-0">
      {events.map((ev, i) => (
        <div key={ev.id} className="flex gap-3">
          <div className="flex flex-col items-center">
            <div className={`w-3 h-3 rounded-full ${i === 0 ? "bg-emerald-500" : "bg-slate-300"}`} />
            {i < events.length - 1 && <div className="w-px flex-1 bg-slate-200" />}
          </div>
          <div className="pb-4 -mt-1">
            <p className="text-sm font-medium text-slate-800">{ev.event_description || ev.event_code || "Event"}</p>
            <p className="text-xs text-slate-400 mt-0.5">
              {ev.event_code ? `${ev.event_code} · ` : ""}
              {ev.location ? `${ev.location} · ` : ""}
              {fmtDateTime(ev.occurred_at)}
              {ev.collapsedCount > 1 ? ` · ${ev.collapsedCount} matching rows collapsed (audit retained)` : ""}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
